/**
 * SettingsPage
 *
 * User settings page with various configuration sections including:
 * - Profile settings
 * - Editor preferences
 * - Connections & Privacy settings
 * - Collaboration settings
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  User,
  Settings2,
  Link2,
  Users,
  Eye,
  EyeOff,
  Mail,
  AtSign,
  Shield,
  Loader2,
  Check,
  Info,
  MessageSquare,
  CheckCheck,
  CircleDot,
  Cloud,
  Unplug,
  Share2,
  X,
  Plus,
  Sun,
  Moon,
  Monitor,
  Palette,
} from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { PersonalAutomationSettings } from '@/components/settings/PersonalAutomationSettings';
import { useOptionalTheme } from '@/design-system/themes/theme-context';
import type { ThemeMode } from '@/design-system/themes/theme-types';
import { useAuthStore } from '@/stores/auth/auth-store';
import { useOrganizationStore } from '@/stores/organization/organization-store';
import { api } from '@/services/api/client';
import { FOREST_GREEN } from '@/styles/tokens';
import * as errorUtils from '@/utils/errors';

// Types for connection preferences
interface ConnectionPreferences {
  searchableByEmail: boolean;
  searchableByUsername: boolean;
  acceptRequests: 'anyone' | 'nobody';
}

// Types for messaging preferences
interface MessagingPreferences {
  allowFromNonConnections: boolean;
  showReadReceipts: boolean;
  showOnlineStatus: boolean;
}

interface UpdatePreferencesResponse {
  success: boolean;
  data?: {
    preferences: {
      connections: ConnectionPreferences;
      messaging: MessagingPreferences;
    };
  };
  error?: string;
}

// Types for storage connections
interface OrgShare {
  orgId: string;
  sharedPaths: string[];
  permissions: 'read' | 'read-write';
  sharedAt?: string;
}

interface StorageConnection {
  integrationId: string;
  isActive: boolean;
  config: { folderPath?: string; driveId?: string; label?: string; orgShares?: OrgShare[] };
  lastConnectedAt?: string;
}

type EmailScopeTier = 'read' | 'read_organize' | 'full';

const isOrgSharePermission = (value: string): value is OrgShare['permissions'] =>
  value === 'read' || value === 'read-write';

const isEmailScopeTier = (value: string): value is EmailScopeTier =>
  value === 'read' || value === 'read_organize' || value === 'full';

const themeOptions: { value: ThemeMode; label: string; icon: React.ReactNode }[] = [
  { value: 'light', label: 'Light', icon: <Sun className="h-4 w-4" /> },
  { value: 'dark', label: 'Dark', icon: <Moon className="h-4 w-4" /> },
  { value: 'system', label: 'System', icon: <Monitor className="h-4 w-4" /> },
];

const AppearanceCard: React.FC = () => {
  const theme = useOptionalTheme();

  if (!theme) {
    return null;
  }

  const { mode, setTheme } = theme;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Palette className="h-5 w-5" />
          Appearance
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2">
          {themeOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setTheme(option.value)}
              aria-pressed={mode === option.value}
              className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors hover:bg-accent/50 ${
                mode === option.value
                  ? 'border-primary bg-primary/5 text-primary font-medium'
                  : 'border-border text-muted-foreground'
              }`}
            >
              {option.icon}
              {option.label}
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export const SettingsPage: React.FC = () => {
  const { user, setUser } = useAuthStore();
  const { organizations } = useOrganizationStore();

  // Connection preferences state
  const [connectionPrefs, setConnectionPrefs] = useState<ConnectionPreferences>({
    searchableByEmail: true,
    searchableByUsername: true,
    acceptRequests: 'anyone',
  });
  const [isLoadingPrefs, setIsLoadingPrefs] = useState(true);
  const [isSavingPrefs, setIsSavingPrefs] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Messaging preferences state
  const [messagingPrefs, setMessagingPrefs] = useState<MessagingPreferences>({
    allowFromNonConnections: false,
    showReadReceipts: true,
    showOnlineStatus: true,
  });
  const [isSavingMessagingPrefs, setIsSavingMessagingPrefs] = useState(false);
  const [hasMessagingChanges, setHasMessagingChanges] = useState(false);

  // Storage connection state
  const [storageConnections, setStorageConnections] = useState<StorageConnection[]>([]);
  const [connectingLabel, setConnectingLabel] = useState<string | null>(null);
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);
  const [folderPathEdits, setFolderPathEdits] = useState<Record<string, string>>({});
  const [newAccountLabel, setNewAccountLabel] = useState('');
  const [showAddAccount, setShowAddAccount] = useState(false);

  // Org sharing state
  const [sharingAccountId, setSharingAccountId] = useState<string | null>(null); // integrationId currently adding share to
  const [shareOrgId, setShareOrgId] = useState('');
  const [sharePath, setSharePath] = useState('');
  const [sharePermissions, setSharePermissions] = useState<'read' | 'read-write'>('read');

  const oneDriveConnections = storageConnections.filter((c) => c.integrationId.startsWith('onedrive:'));

  // ─── Email integration state ─────────────────────────────────
  interface EmailAccount {
    accountId: string;
    provider: 'microsoft' | 'gmail';
    displayName: string;
    emailAddress: string;
    connectionStatus: 'active' | 'needs_reauth' | 'disabled';
    expiresAt: string;
    lastSyncedAt?: string;
    grantedOauthScopes: string[];
    config: {
      sendMode: 'drafts_only' | 'direct_send';
      permanentDeleteEnabled: boolean;
      forwardEnabled: boolean;
      moveToFolderEnabled: boolean;
      archiveEnabled: boolean;
      trashEnabled: boolean;
      markReadEnabled: boolean;
      pollingEnabled: boolean;
      pollIntervalSeconds: number;
      monitoredFolders: string[];
      activeHoursStart: string;
      activeHoursEnd: string;
      activeHoursTimezone: string;
    };
    createdAt: string;
  }

  const [emailAccounts, setEmailAccounts] = useState<EmailAccount[]>([]);
  const [emailConnectingProvider, setEmailConnectingProvider] = useState<'microsoft' | 'gmail' | null>(null);
  const [emailDisconnectingId, setEmailDisconnectingId] = useState<string | null>(null);
  const [showEmailScopePicker, setShowEmailScopePicker] = useState(false);
  const [emailScopeTier, setEmailScopeTier] = useState<'read' | 'read_organize' | 'full'>('full');
  const [emailDisplayName, setEmailDisplayName] = useState('');

  const fetchStorageConnections = useCallback(async () => {
    try {
      const response = await api.get<StorageConnection[]>('/storage-connections');
      if (response.success && response.data) {
        const connections = response.data;
        setStorageConnections(connections);
        const edits: Record<string, string> = {};
        for (const c of connections) {
          if (c.config?.folderPath) edits[c.integrationId] = c.config.folderPath;
        }
        setFolderPathEdits(edits);
      }
    } catch {
      // Silently fail — connections section just won't show connected state
    }
  }, []);

  const handleOneDriveConnect = useCallback(async (label: string) => {
    // Client-side label validation
    if (!/^[a-zA-Z0-9_-]{1,30}$/.test(label)) {
      toast.error('Label must be 1-30 characters: letters, numbers, hyphens, or underscores only');
      return;
    }

    setConnectingLabel(label);
    try {
      const response = await api.get<{ authorizationUrl: string }>(
        `/storage-connections/onedrive/authorize?label=${encodeURIComponent(label)}`
      );
      if (!response.success || !response.data?.authorizationUrl) {
        toast.error('Failed to start OneDrive connection');
        setConnectingLabel(null);
        return;
      }

      // Open popup for Microsoft login
      const popup = window.open(
        response.data.authorizationUrl,
        'onedrive-auth',
        'width=600,height=700,scrollbars=yes'
      );

      // Listen for postMessage from the callback popup
      const handleMessage = (event: MessageEvent) => {
        if (event.data?.type === 'onedrive-connected') {
          toast.success(`OneDrive account '${event.data.label || label}' connected`);
          fetchStorageConnections();
          setConnectingLabel(null);
          setNewAccountLabel('');
          setShowAddAccount(false);
          window.removeEventListener('message', handleMessage);
        } else if (event.data?.type === 'onedrive-error') {
          toast.error(event.data.message || 'OneDrive connection failed');
          setConnectingLabel(null);
          window.removeEventListener('message', handleMessage);
        }
      };
      window.addEventListener('message', handleMessage);

      // Also handle popup being closed manually
      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed);
          setTimeout(() => {
            setConnectingLabel(null);
            window.removeEventListener('message', handleMessage);
          }, 1000);
        }
      }, 500);
    } catch (err: unknown) {
      toast.error(errorUtils.getErrorMessageOrFallback(err, 'Failed to start OneDrive connection'));
      setConnectingLabel(null);
    }
  }, [fetchStorageConnections]);

  // ─── Email integration handlers ────────────────────────────

  const fetchEmailAccounts = useCallback(async () => {
    try {
      const response = await api.get<EmailAccount[]>('/integrations/email/accounts');
      if (response.success && response.data) {
        setEmailAccounts(response.data);
      }
    } catch {
      // Silently fail — section just won't show connected accounts
    }
  }, []);

  const handleEmailConnect = useCallback(
    async (provider: 'microsoft' | 'gmail', scopeTier: 'read' | 'read_organize' | 'full', displayName: string) => {
      setEmailConnectingProvider(provider);
      try {
        const params = new URLSearchParams({
          provider,
          scopeTier,
          displayName: displayName || `${provider === 'microsoft' ? 'Microsoft 365' : 'Gmail'}`,
        });
        const response = await api.get<{ authorizationUrl: string }>(
          `/integrations/email/authorize?${params.toString()}`,
        );
        if (!response.success || !response.data?.authorizationUrl) {
          toast.error('Failed to start email connection');
          setEmailConnectingProvider(null);
          return;
        }

        const popup = window.open(
          response.data.authorizationUrl,
          'email-auth',
          'width=600,height=720,scrollbars=yes',
        );

        const handleMessage = (event: MessageEvent) => {
          if (event.data?.type === 'email-connected') {
            toast.success(`Email account '${event.data.emailAddress || event.data.displayName}' connected`);
            fetchEmailAccounts();
            setEmailConnectingProvider(null);
            setShowEmailScopePicker(false);
            setEmailDisplayName('');
            window.removeEventListener('message', handleMessage);
          } else if (event.data?.type === 'email-error') {
            toast.error(event.data.message || 'Email connection failed');
            setEmailConnectingProvider(null);
            window.removeEventListener('message', handleMessage);
          }
        };
        window.addEventListener('message', handleMessage);

        const checkClosed = setInterval(() => {
          if (popup?.closed) {
            clearInterval(checkClosed);
            setTimeout(() => {
              setEmailConnectingProvider(null);
              window.removeEventListener('message', handleMessage);
            }, 1000);
          }
        }, 500);
      } catch (err: unknown) {
        toast.error(errorUtils.getErrorMessageOrFallback(err, 'Failed to start email connection'));
        setEmailConnectingProvider(null);
      }
    },
    [fetchEmailAccounts],
  );

  const handleEmailDisconnect = useCallback(async (accountId: string, emailAddress: string) => {
    setEmailDisconnectingId(accountId);
    try {
      await api.delete(`/integrations/email/accounts/${accountId}`);
      toast.success(`Email account '${emailAddress}' disconnected`);
      setEmailAccounts((prev) => prev.filter((a) => a.accountId !== accountId));
    } catch {
      toast.error('Failed to disconnect email account');
    }
    setEmailDisconnectingId(null);
  }, []);

  const handleEmailConfigToggle = useCallback(
    async (accountId: string, key: string, value: unknown) => {
      try {
        const response = await api.patch<EmailAccount>(`/integrations/email/accounts/${accountId}`, {
          config: { [key]: value },
        });
        const updatedAccount = response.data;
        if (response.success && updatedAccount) {
          setEmailAccounts((prev) =>
            prev.map((a) => (a.accountId === accountId ? updatedAccount : a)),
          );
          toast.success('Setting updated');
        }
      } catch (err: unknown) {
        toast.error(errorUtils.getErrorMessageOrFallback(err, 'Failed to update setting'));
      }
    },
    [],
  );

  const handleOneDriveDisconnect = useCallback(async (label: string, integrationId: string) => {
    setDisconnectingId(integrationId);
    try {
      await api.delete(`/storage-connections/onedrive/${encodeURIComponent(label)}`);
      toast.success(`OneDrive account '${label}' disconnected`);
      setStorageConnections((prev) => prev.filter((c) => c.integrationId !== integrationId));
      setFolderPathEdits((prev) => {
        const { [integrationId]: _removedFolderPath, ...remainingFolderPathEdits } = prev;
        return remainingFolderPathEdits;
      });
    } catch {
      toast.error('Failed to disconnect OneDrive');
    }
    setDisconnectingId(null);
  }, []);

  const handleFolderPathSave = useCallback(async (label: string, integrationId: string) => {
    const folderPath = folderPathEdits[integrationId]?.trim();
    if (!folderPath) return;
    try {
      await api.patch(`/storage-connections/onedrive/${encodeURIComponent(label)}`, { folderPath });
      toast.success('Folder path updated');
      fetchStorageConnections();
    } catch {
      toast.error('Failed to update folder path');
    }
  }, [folderPathEdits, fetchStorageConnections]);

  const handleAddOrgShare = useCallback(async (label: string) => {
    if (!shareOrgId || !sharePath.trim()) return;
    try {
      await api.post(`/storage-connections/onedrive/${encodeURIComponent(label)}/org-shares`, {
        orgId: shareOrgId,
        paths: [sharePath.trim().startsWith('/') ? sharePath.trim() : `/${sharePath.trim()}`],
        permissions: sharePermissions,
      });
      toast.success('Folder shared with organization');
      setSharingAccountId(null);
      setShareOrgId('');
      setSharePath('');
      setSharePermissions('read');
      fetchStorageConnections();
    } catch (err: unknown) {
      toast.error(errorUtils.getErrorMessageOrFallback(err, 'Failed to share folder'));
    }
  }, [shareOrgId, sharePath, sharePermissions, fetchStorageConnections]);

  const handleToggleSharePermissions = useCallback(async (label: string, share: OrgShare) => {
    const newPermissions = share.permissions === 'read' ? 'read-write' : 'read';
    try {
      await api.post(`/storage-connections/onedrive/${encodeURIComponent(label)}/org-shares`, {
        orgId: share.orgId,
        paths: share.sharedPaths,
        permissions: newPermissions,
      });
      toast.success(`Permissions updated to ${newPermissions}`);
      fetchStorageConnections();
    } catch (err: unknown) {
      toast.error(errorUtils.getErrorMessageOrFallback(err, 'Failed to update permissions'));
    }
  }, [fetchStorageConnections]);

  const handleRemoveOrgShare = useCallback(async (label: string, orgId: string) => {
    try {
      await api.delete(`/storage-connections/onedrive/${encodeURIComponent(label)}/org-shares/${orgId}`);
      toast.success('Sharing revoked');
      fetchStorageConnections();
    } catch {
      toast.error('Failed to revoke sharing');
    }
  }, [fetchStorageConnections]);

  // Load preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      setIsLoadingPrefs(true);

      try {
        console.log('[SettingsPrivacy] Loading preferences...');

        // Get preferences from user object or fetch from API
        const userConnectionPrefs = user?.preferences?.connections;
        const userMessagingPrefs = user?.preferences?.messaging;

        if (userConnectionPrefs) {
          setConnectionPrefs({
            searchableByEmail: userConnectionPrefs.searchableByEmail ?? true,
            searchableByUsername: userConnectionPrefs.searchableByUsername ?? true,
            acceptRequests: userConnectionPrefs.acceptRequests ?? 'anyone',
          });
          console.log('[SettingsPrivacy] Loaded connection preferences from user store:', userConnectionPrefs);
        }

        if (userMessagingPrefs) {
          setMessagingPrefs({
            allowFromNonConnections: userMessagingPrefs.allowFromNonConnections ?? false,
            showReadReceipts: userMessagingPrefs.showReadReceipts ?? true,
            showOnlineStatus: userMessagingPrefs.showOnlineStatus ?? true,
          });
          console.log('[SettingsPrivacy] Loaded messaging preferences from user store:', userMessagingPrefs);
        }

        // Fetch from API if not in user store
        if (!userConnectionPrefs || !userMessagingPrefs) {
          const response = await api.get<UpdatePreferencesResponse['data']>('/users/me/preferences');
          if (response.success && response.data?.preferences) {
            const apiConnPrefs = response.data.preferences.connections;
            const apiMsgPrefs = response.data.preferences.messaging;

            if (apiConnPrefs && !userConnectionPrefs) {
              setConnectionPrefs({
                searchableByEmail: apiConnPrefs.searchableByEmail ?? true,
                searchableByUsername: apiConnPrefs.searchableByUsername ?? true,
                acceptRequests: apiConnPrefs.acceptRequests ?? 'anyone',
              });
              console.log('[SettingsPrivacy] Loaded connection preferences from API:', apiConnPrefs);
            }

            if (apiMsgPrefs && !userMessagingPrefs) {
              setMessagingPrefs({
                allowFromNonConnections: apiMsgPrefs.allowFromNonConnections ?? false,
                showReadReceipts: apiMsgPrefs.showReadReceipts ?? true,
                showOnlineStatus: apiMsgPrefs.showOnlineStatus ?? true,
              });
              console.log('[SettingsPrivacy] Loaded messaging preferences from API:', apiMsgPrefs);
            }
          }
        }
      } catch (err) {
        console.error('[SettingsPrivacy] Error loading preferences:', err);
        toast.error('Failed to load privacy settings');
      } finally {
        setIsLoadingPrefs(false);
      }
    };

    loadPreferences();
    fetchStorageConnections();
    fetchEmailAccounts();
  }, [user, fetchStorageConnections, fetchEmailAccounts]);

  // Handle preference changes
  const handlePrefChange = <K extends keyof ConnectionPreferences>(
    key: K,
    value: ConnectionPreferences[K]
  ) => {
    console.log('[SettingsPrivacy] Updating preference:', key, '=', value);
    setConnectionPrefs((prev) => ({
      ...prev,
      [key]: value,
    }));
    setHasChanges(true);
  };

  // Save preferences
  const handleSavePreferences = async () => {
    setIsSavingPrefs(true);

    try {
      console.log('[SettingsPrivacy] Saving connection preferences:', connectionPrefs);

      const response = await api.patch<UpdatePreferencesResponse['data']>(
        '/users/me/preferences/connections',
        connectionPrefs
      );

      if (response.success) {
        // Update local user store
        if (user) {
          setUser({
            ...user,
            preferences: {
              ...user.preferences,
              connections: connectionPrefs,
            },
          });
        }

        toast.success('Privacy settings saved');
        setHasChanges(false);
        console.log('[SettingsPrivacy] Preferences saved successfully');
      } else {
        throw new Error(response.error || 'Failed to save preferences');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save settings';
      console.error('[SettingsPrivacy] Error saving preferences:', message);
      toast.error('Failed to save settings', {
        description: message,
      });
    } finally {
      setIsSavingPrefs(false);
    }
  };

  // Handle messaging preference changes
  const handleMessagingPrefChange = <K extends keyof MessagingPreferences>(
    key: K,
    value: MessagingPreferences[K]
  ) => {
    console.log('[SettingsMessaging] Updating preference:', key, '=', value);
    setMessagingPrefs((prev) => ({
      ...prev,
      [key]: value,
    }));
    setHasMessagingChanges(true);
  };

  // Save messaging preferences
  const handleSaveMessagingPreferences = async () => {
    setIsSavingMessagingPrefs(true);

    try {
      console.log('[SettingsMessaging] Saving messaging preferences:', messagingPrefs);

      const response = await api.patch<UpdatePreferencesResponse['data']>(
        '/users/me/preferences/messaging',
        messagingPrefs
      );

      if (response.success) {
        // Update local user store
        if (user) {
          setUser({
            ...user,
            preferences: {
              ...user.preferences,
              messaging: messagingPrefs,
            },
          });
        }

        toast.success('Messaging settings saved');
        setHasMessagingChanges(false);
        console.log('[SettingsMessaging] Preferences saved successfully');
      } else {
        throw new Error(response.error || 'Failed to save preferences');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save settings';
      console.error('[SettingsMessaging] Error saving preferences:', message);
      toast.error('Failed to save messaging settings', {
        description: message,
      });
    } finally {
      setIsSavingMessagingPrefs(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Settings</h1>
        <p className="text-muted-foreground">
          Configure your workspace and preferences
        </p>
      </div>

      <div className="grid gap-6">
        {/* Profile Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Profile
            </CardTitle>
            <CardDescription>Manage your account information</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Profile settings coming soon...
            </p>
          </CardContent>
        </Card>

        {/* Editor Preferences Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5" />
              Editor Preferences
            </CardTitle>
            <CardDescription>Customize your editor experience</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Editor preferences coming soon...
            </p>
          </CardContent>
        </Card>

        {/* Appearance Card */}
        <AppearanceCard />

        <PersonalAutomationSettings />

        {/* Connections & Privacy Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Connections & Privacy
            </CardTitle>
            <CardDescription>
              Control how others can find and connect with you
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoadingPrefs ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">
                  Loading settings...
                </span>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Discoverability Section */}
                <div>
                  <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                    <Eye className="h-4 w-4" />
                    Discoverability
                  </h3>

                  <div className="space-y-4">
                    {/* Searchable by email */}
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <Label htmlFor="searchable-email" className="font-medium">
                            Searchable by email
                          </Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Info className="h-3.5 w-3.5 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs">
                                  Allow others to find you by searching for your email address
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Others can find you by your email address
                        </p>
                      </div>
                      <Switch
                        id="searchable-email"
                        checked={connectionPrefs.searchableByEmail}
                        onCheckedChange={(checked) =>
                          handlePrefChange('searchableByEmail', checked)
                        }
                      />
                    </div>

                    {/* Searchable by username */}
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2">
                          <AtSign className="h-4 w-4 text-muted-foreground" />
                          <Label htmlFor="searchable-username" className="font-medium">
                            Searchable by username
                          </Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Info className="h-3.5 w-3.5 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs">
                                  Allow others to find you by searching for your username
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Others can find you by your username
                        </p>
                      </div>
                      <Switch
                        id="searchable-username"
                        checked={connectionPrefs.searchableByUsername}
                        onCheckedChange={(checked) =>
                          handlePrefChange('searchableByUsername', checked)
                        }
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Connection Requests Section */}
                <div>
                  <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                    <Link2 className="h-4 w-4" />
                    Connection Requests
                  </h3>

                  <div className="space-y-4">
                    {/* Who can send requests */}
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <Label htmlFor="accept-requests" className="font-medium">
                            Accept connection requests from
                          </Label>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Control who can send you connection requests
                        </p>
                      </div>
                      <Select
                        value={connectionPrefs.acceptRequests}
                        onValueChange={(value: 'anyone' | 'nobody') =>
                          handlePrefChange('acceptRequests', value)
                        }
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="anyone">
                            <div className="flex items-center gap-2">
                              <Users className="h-4 w-4" />
                              Anyone
                            </div>
                          </SelectItem>
                          <SelectItem value="nobody">
                            <div className="flex items-center gap-2">
                              <EyeOff className="h-4 w-4" />
                              Nobody
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Warning for "nobody" setting */}
                    {connectionPrefs.acceptRequests === 'nobody' && (
                      <div
                        className="flex items-start gap-2 p-3 rounded-lg text-sm"
                        style={{
                          backgroundColor: FOREST_GREEN.faint,
                          border: `1px solid ${FOREST_GREEN.DEFAULT}20`,
                        }}
                      >
                        <Info
                          className="h-4 w-4 mt-0.5 flex-shrink-0"
                          style={{ color: FOREST_GREEN.DEFAULT }}
                        />
                        <p style={{ color: FOREST_GREEN.DEFAULT }}>
                          With this setting, no one will be able to send you connection requests.
                          You can still send requests to others.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Save button */}
                <div className="flex justify-end pt-4">
                  <Button
                    onClick={handleSavePreferences}
                    disabled={!hasChanges || isSavingPrefs}
                    style={{
                      backgroundColor: hasChanges ? FOREST_GREEN.DEFAULT : undefined,
                      color: hasChanges ? 'white' : undefined,
                    }}
                  >
                    {isSavingPrefs ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Messaging Preferences Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Messaging
            </CardTitle>
            <CardDescription>
              Control how messaging works and what others can see
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoadingPrefs ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">
                  Loading settings...
                </span>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Message Settings Section */}
                <div>
                  <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Who Can Message You
                  </h3>

                  <div className="space-y-4">
                    {/* Allow messages from non-connections */}
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2">
                          <Link2 className="h-4 w-4 text-muted-foreground" />
                          <Label htmlFor="allow-non-connections" className="font-medium">
                            Allow messages from non-connections
                          </Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Info className="h-3.5 w-3.5 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs">
                                  Allow users who aren't in your connections to send you messages
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Currently, only your connections can message you
                        </p>
                      </div>
                      <Switch
                        id="allow-non-connections"
                        checked={messagingPrefs.allowFromNonConnections}
                        onCheckedChange={(checked) =>
                          handleMessagingPrefChange('allowFromNonConnections', checked)
                        }
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Privacy Section */}
                <div>
                  <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                    <Eye className="h-4 w-4" />
                    Privacy
                  </h3>

                  <div className="space-y-4">
                    {/* Show read receipts */}
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2">
                          <CheckCheck className="h-4 w-4 text-muted-foreground" />
                          <Label htmlFor="show-read-receipts" className="font-medium">
                            Show read receipts
                          </Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Info className="h-3.5 w-3.5 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs">
                                  Let others see when you've read their messages
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Others will see when you've read their messages
                        </p>
                      </div>
                      <Switch
                        id="show-read-receipts"
                        checked={messagingPrefs.showReadReceipts}
                        onCheckedChange={(checked) =>
                          handleMessagingPrefChange('showReadReceipts', checked)
                        }
                      />
                    </div>

                    {/* Show online status */}
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2">
                          <CircleDot className="h-4 w-4 text-muted-foreground" />
                          <Label htmlFor="show-online-status" className="font-medium">
                            Show online status
                          </Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Info className="h-3.5 w-3.5 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs">
                                  Let others see when you're online
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Others will see a green dot when you're online
                        </p>
                      </div>
                      <Switch
                        id="show-online-status"
                        checked={messagingPrefs.showOnlineStatus}
                        onCheckedChange={(checked) =>
                          handleMessagingPrefChange('showOnlineStatus', checked)
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Save button */}
                <div className="flex justify-end pt-4">
                  <Button
                    onClick={handleSaveMessagingPreferences}
                    disabled={!hasMessagingChanges || isSavingMessagingPrefs}
                    style={{
                      backgroundColor: hasMessagingChanges ? FOREST_GREEN.DEFAULT : undefined,
                      color: hasMessagingChanges ? 'white' : undefined,
                    }}
                  >
                    {isSavingMessagingPrefs ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Connected Storage Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Cloud className="h-5 w-5" />
              Connected Storage
            </CardTitle>
            <CardDescription>
              Connect personal cloud storage for your private files.
              Files are stored unencrypted so they stay accessible from your desktop sync app.
              You can connect multiple OneDrive accounts.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Connected OneDrive accounts */}
              {oneDriveConnections.map((conn) => {
                const label = conn.config?.label || conn.integrationId.replace('onedrive:', '');
                return (
                  <div key={conn.integrationId}>
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded bg-[#0078D4] flex items-center justify-center text-white text-xs font-bold">
                          OD
                        </div>
                        <div>
                          <p className="font-medium text-sm">
                            OneDrive — {label}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Connected {conn.lastConnectedAt
                              ? new Date(conn.lastConnectedAt).toLocaleDateString()
                              : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50">
                          Connected
                        </Badge>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleOneDriveDisconnect(label, conn.integrationId)}
                          disabled={disconnectingId === conn.integrationId}
                        >
                          {disconnectingId === conn.integrationId ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Unplug className="h-3 w-3 mr-1" />
                          )}
                          Disconnect
                        </Button>
                      </div>
                    </div>
                    {/* Folder Path for this account */}
                    <div className="flex items-center gap-2 pl-4 mt-2">
                      <Label className="text-sm text-muted-foreground whitespace-nowrap">
                        Folder:
                      </Label>
                      <Input
                        value={folderPathEdits[conn.integrationId] ?? ''}
                        onChange={(e) =>
                          setFolderPathEdits((prev) => ({ ...prev, [conn.integrationId]: e.target.value }))
                        }
                        placeholder="/TerraMatrix"
                        className="h-8 max-w-[240px] text-sm"
                      />
                      {folderPathEdits[conn.integrationId] !== conn.config?.folderPath && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8"
                          onClick={() => handleFolderPathSave(label, conn.integrationId)}
                        >
                          <Check className="h-3 w-3 mr-1" />
                          Save
                        </Button>
                      )}
                    </div>

                    {/* Org Sharing Section */}
                    <div className="pl-4 mt-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <Share2 className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-xs font-medium text-muted-foreground">Shared with Organizations</span>
                      </div>

                      {/* Existing shares */}
                      {(conn.config?.orgShares || []).map((share) => {
                        const org = organizations.find((o) => o._id === share.orgId);
                        return (
                          <div key={share.orgId} className="flex items-center gap-2 pl-5 text-xs">
                            <Badge variant="secondary" className="text-xs py-0">
                              {org?.name || share.orgId}
                            </Badge>
                            <span className="text-muted-foreground truncate max-w-[200px]">
                              {share.sharedPaths.join(', ')}
                            </span>
                            <button
                              onClick={() => handleToggleSharePermissions(label, share)}
                              className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer underline decoration-dotted"
                              title="Click to toggle permissions"
                            >
                              ({share.permissions})
                            </button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-5 w-5 p-0"
                              onClick={() => handleRemoveOrgShare(label, share.orgId)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        );
                      })}

                      {/* Add share inline form */}
                      {sharingAccountId === conn.integrationId ? (
                        <div className="flex items-center gap-2 pl-5">
                          <Select
                            value={shareOrgId}
                            onValueChange={setShareOrgId}
                          >
                            <SelectTrigger className="h-7 w-[160px] text-xs">
                              <SelectValue placeholder="Select org" />
                            </SelectTrigger>
                            <SelectContent>
                              {organizations.map((org) => (
                                <SelectItem key={org._id} value={org._id}>
                                  {org.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            value={sharePath}
                            onChange={(e) => setSharePath(e.target.value)}
                            placeholder={conn.config?.folderPath || '/TerraMatrix'}
                            className="h-7 w-[180px] text-xs"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleAddOrgShare(label);
                              if (e.key === 'Escape') { setSharingAccountId(null); setShareOrgId(''); setSharePath(''); setSharePermissions('read'); }
                            }}
                          />
                          <Select
                            value={sharePermissions}
                            onValueChange={(value) => {
                              if (isOrgSharePermission(value)) {
                                setSharePermissions(value);
                              }
                            }}
                          >
                            <SelectTrigger className="h-7 w-[120px] text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="read">Read only</SelectItem>
                              <SelectItem value="read-write">Read & Write</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            size="sm"
                            className="h-7 text-xs"
                            disabled={!shareOrgId || !sharePath.trim()}
                            onClick={() => handleAddOrgShare(label)}
                            style={{ backgroundColor: FOREST_GREEN.DEFAULT, color: 'white' }}
                          >
                            Share
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs"
                            onClick={() => { setSharingAccountId(null); setShareOrgId(''); setSharePath(''); setSharePermissions('read'); }}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 text-xs pl-5"
                          onClick={() => setSharingAccountId(conn.integrationId)}
                          disabled={organizations.length === 0}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Share folder with org
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* No accounts connected message */}
              {oneDriveConnections.length === 0 && !showAddAccount && (
                <div className="flex items-center justify-between p-4 border rounded-lg border-dashed">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-[#0078D4] flex items-center justify-center text-white text-xs font-bold">
                      OD
                    </div>
                    <div>
                      <p className="font-medium text-sm">Microsoft OneDrive</p>
                      <p className="text-xs text-muted-foreground">
                        Store files in your personal OneDrive accounts
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Add Account inline form */}
              {showAddAccount && (
                <div className="flex items-center gap-2 p-4 border rounded-lg border-dashed">
                  <div className="w-8 h-8 rounded bg-[#0078D4] flex items-center justify-center text-white text-xs font-bold shrink-0">
                    OD
                  </div>
                  <Input
                    value={newAccountLabel}
                    onChange={(e) => setNewAccountLabel(e.target.value)}
                    placeholder="Account label (e.g. terramatrix, personal)"
                    className="h-8 max-w-[240px] text-sm"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newAccountLabel.trim()) {
                        handleOneDriveConnect(newAccountLabel.trim());
                      } else if (e.key === 'Escape') {
                        setShowAddAccount(false);
                        setNewAccountLabel('');
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    disabled={!newAccountLabel.trim() || connectingLabel !== null}
                    onClick={() => handleOneDriveConnect(newAccountLabel.trim())}
                    style={{
                      backgroundColor: FOREST_GREEN.DEFAULT,
                      color: 'white',
                    }}
                  >
                    {connectingLabel ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      'Connect'
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setShowAddAccount(false); setNewAccountLabel(''); }}
                    disabled={connectingLabel !== null}
                  >
                    Cancel
                  </Button>
                </div>
              )}

              {/* Add OneDrive Account button */}
              {!showAddAccount && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddAccount(true)}
                  className="w-full"
                >
                  <Cloud className="h-3 w-3 mr-1" />
                  Add OneDrive Account
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Email Integration Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Connected Email
            </CardTitle>
            <CardDescription>
              Connect Microsoft 365 or Gmail accounts so your agents can read and (optionally) act on
              your inbox. You can connect multiple accounts and control exactly what each agent is
              allowed to do per account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Connected email accounts */}
              {emailAccounts.map((acct) => (
                <div key={acct.accountId} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-8 h-8 rounded flex items-center justify-center text-white text-xs font-bold ${
                          acct.provider === 'microsoft' ? 'bg-[#0078D4]' : 'bg-[#EA4335]'
                        }`}
                      >
                        {acct.provider === 'microsoft' ? 'M' : 'G'}
                      </div>
                      <div>
                        <p className="font-medium text-sm">
                          {acct.displayName}{' '}
                          {acct.connectionStatus === 'needs_reauth' && (
                            <Badge variant="destructive" className="ml-1 text-[10px]">
                              Needs reconnect
                            </Badge>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">{acct.emailAddress}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEmailDisconnect(acct.accountId, acct.emailAddress)}
                      disabled={emailDisconnectingId === acct.accountId}
                    >
                      {emailDisconnectingId === acct.accountId ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Unplug className="h-4 w-4" />
                      )}
                    </Button>
                  </div>

                  {/* Capability switches */}
                  <Separator />
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase">
                      What this agent can do
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center justify-between">
                        <Label htmlFor={`${acct.accountId}-send-mode`} className="text-sm">
                          Direct send
                        </Label>
                        <Switch
                          id={`${acct.accountId}-send-mode`}
                          checked={acct.config.sendMode === 'direct_send'}
                          onCheckedChange={(checked) =>
                            handleEmailConfigToggle(
                              acct.accountId,
                              'sendMode',
                              checked ? 'direct_send' : 'drafts_only',
                            )
                          }
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor={`${acct.accountId}-forward`} className="text-sm">
                          Allow forwarding
                        </Label>
                        <Switch
                          id={`${acct.accountId}-forward`}
                          checked={acct.config.forwardEnabled}
                          onCheckedChange={(checked) =>
                            handleEmailConfigToggle(acct.accountId, 'forwardEnabled', checked)
                          }
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor={`${acct.accountId}-move`} className="text-sm">
                          Move to folders
                        </Label>
                        <Switch
                          id={`${acct.accountId}-move`}
                          checked={acct.config.moveToFolderEnabled}
                          onCheckedChange={(checked) =>
                            handleEmailConfigToggle(acct.accountId, 'moveToFolderEnabled', checked)
                          }
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor={`${acct.accountId}-archive`} className="text-sm">
                          Archive
                        </Label>
                        <Switch
                          id={`${acct.accountId}-archive`}
                          checked={acct.config.archiveEnabled}
                          onCheckedChange={(checked) =>
                            handleEmailConfigToggle(acct.accountId, 'archiveEnabled', checked)
                          }
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor={`${acct.accountId}-trash`} className="text-sm">
                          Move to trash
                        </Label>
                        <Switch
                          id={`${acct.accountId}-trash`}
                          checked={acct.config.trashEnabled}
                          onCheckedChange={(checked) =>
                            handleEmailConfigToggle(acct.accountId, 'trashEnabled', checked)
                          }
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor={`${acct.accountId}-permanent-delete`} className="text-sm">
                          Permanent delete
                        </Label>
                        <Switch
                          id={`${acct.accountId}-permanent-delete`}
                          checked={acct.config.permanentDeleteEnabled}
                          onCheckedChange={(checked) =>
                            handleEmailConfigToggle(
                              acct.accountId,
                              'permanentDeleteEnabled',
                              checked,
                            )
                          }
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor={`${acct.accountId}-polling`} className="text-sm">
                          Auto-monitor inbox
                        </Label>
                        <Switch
                          id={`${acct.accountId}-polling`}
                          checked={acct.config.pollingEnabled}
                          onCheckedChange={(checked) =>
                            handleEmailConfigToggle(acct.accountId, 'pollingEnabled', checked)
                          }
                        />
                      </div>
                    </div>
                    <p className="text-[11px] text-muted-foreground italic">
                      Direct send is OFF by default — agents create drafts for you to review.
                      Permanent delete is OFF by default — deleted emails always go to trash.
                    </p>
                  </div>
                </div>
              ))}

              {/* Empty state */}
              {emailAccounts.length === 0 && !showEmailScopePicker && (
                <div className="flex items-center justify-between p-4 border rounded-lg border-dashed">
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">No email accounts connected</p>
                  </div>
                </div>
              )}

              {/* Connect — scope picker form */}
              {showEmailScopePicker && (
                <div className="p-4 border rounded-lg border-dashed space-y-3">
                  <div className="space-y-1">
                    <Label htmlFor="email-display-name" className="text-sm">
                      Account label
                    </Label>
                    <Input
                      id="email-display-name"
                      value={emailDisplayName}
                      onChange={(e) => setEmailDisplayName(e.target.value)}
                      placeholder="e.g. Work, Personal"
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Access level</Label>
                    <Select
                      value={emailScopeTier}
                      onValueChange={(value) => {
                        if (isEmailScopeTier(value)) {
                          setEmailScopeTier(value);
                        }
                      }}
                    >
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="read">Read only — agent can read mail but not modify it</SelectItem>
                        <SelectItem value="read_organize">
                          Read &amp; organize — read, mark, move, archive, but never send
                        </SelectItem>
                        <SelectItem value="full">
                          Full access — read, organize, and send (with safety switches)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() =>
                        handleEmailConnect('microsoft', emailScopeTier, emailDisplayName.trim())
                      }
                      disabled={!emailDisplayName.trim() || emailConnectingProvider !== null}
                      style={{ backgroundColor: FOREST_GREEN.DEFAULT, color: 'white' }}
                    >
                      {emailConnectingProvider === 'microsoft' ? (
                        <>
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        <>
                          <span className="w-4 h-4 mr-1 rounded bg-[#0078D4] inline-flex items-center justify-center text-[8px] font-bold text-white">
                            M
                          </span>
                          Microsoft 365
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled
                      title="Gmail support coming soon"
                    >
                      <span className="w-4 h-4 mr-1 rounded bg-[#EA4335] inline-flex items-center justify-center text-[8px] font-bold text-white">
                        G
                      </span>
                      Gmail (soon)
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setShowEmailScopePicker(false);
                        setEmailDisplayName('');
                      }}
                      disabled={emailConnectingProvider !== null}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* Add account button */}
              {!showEmailScopePicker && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowEmailScopePicker(true)}
                  className="w-full"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Connect Email Account
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Collaboration Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Collaboration
            </CardTitle>
            <CardDescription>Manage team and sharing settings</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Collaboration settings coming soon...
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Version */}
      <div className="text-center text-xs text-muted-foreground pt-4 pb-2">
        {import.meta.env.VITE_APP_VERSION || 'dev'}
      </div>
    </div>
  );
};
